import {
  createPublicClient,
  createWalletClient,
  custom,
  getAddress,
  http,
  parseAbi,
  parseAbiItem,
} from "viem";
import { arbitrum } from "viem/chains";
import {
  ERC1155_APPROVAL_ABI,
  ERC1155_BALANCE_ABI,
  SUPERNOVA_CHAIN_ID,
  SUPERNOVA_COLLECTION_ADDRESS,
  SUPERNOVA_STAKING_ADDRESS,
  SUPERNOVA_STAKING_DEPLOY_BLOCK,
  SUPERNOVA_TOKENS,
  SUPERNOVA_TOKEN_IDS,
} from "./supernovaContracts";
import {
  TOKEN_EMERGENCY_UNSTAKED_EVENT,
  TOKEN_STAKED_EVENT,
  TOKEN_UNSTAKED_EVENT,
  buildClosedLots,
  decodeExitLog,
  exitBlockKeys,
} from "./supernovaClosedLots";
import { SUPERNOVA_STAKING_READ } from "./supernovaStakeRead";
import { SUPERNOVA_ERROR, SupernovaChainError } from "./supernovaChainError";
import {
  buildApprovalSignaturePayload,
  buildStakeSignaturePayload,
  buildUnstakeSignaturePayload,
  logSignatureRequest,
} from "./supernovaSignatureRequest";
import {
  assertApprovalPayloadMatches,
  assertStakePayloadMatches,
  assertUnstakePayloadMatches,
  assertWriteTarget,
} from "./supernovaWriteGuards";
import {
  getStakingWriteAbi,
  stakeAmountArg,
  stakeDurationSeconds,
  stakeIdArg,
  stakeTokenIdArg,
} from "./supernovaWriteArgs";
import { isSupernovaBuyEnabled } from "../../config/supernovaFlags";
import { normalizeLot } from "../../config/supernovaXp";

/**
 * supernovaChain — read-only chain access for the Supernova tab.
 *
 * What is REAL here:
 *   getOwned(wallet)          — ERC-1155 balanceOfBatch over ids 7–10.
 *   getStaked(wallet)         — getUserStakes + stakes on the staking contract,
 *                               decoded by supernovaStakeRead.
 *   getClosedStakes(wallet)   — the member's CLOSED lots, rebuilt from the
 *                               contract's own TokenStaked / TokenUnstaked /
 *                               TokenEmergencyUnstaked logs plus the exit
 *                               blocks' timestamps. Decoded by
 *                               supernovaClosedLots; this is the half `stakes()`
 *                               cannot answer, because storage keeps a closed
 *                               lot's fields but not the moment it closed.
 *   getApprovalStatus(wallet) — ERC-1155 isApprovedForAll on the collection.
 * What is REAL AND LIVE as of the supervised signing review:
 *   setApprovalForAll         — the one-time operator approval.
 *   stake / unstake /         — the three member-facing writes on
 *   emergencyUnstake            ARCNFTStakeProtocol.
 *
 *   All four are MEMBER-SIGNED. The UI reaches them through requestStakeSignature
 *   / requestUnstakeSignature / requestApprovalSignature below, which are no
 *   longer refusals: each one builds the payload that describes the transaction,
 *   runs it past supernovaWriteGuards, dry-runs it against the live contract, and
 *   only then hands it to the member's own wallet. This wiring was done under
 *   supervision, which is what CLAUDE.md hard stop 1 requires.
 *
 *   THE ABI THIS SIGNS AGAINST IS VERIFIED FROM TWO INDEPENDENT SOURCES —
 *   ARC's own live dApp bundle and the deployed runtime bytecode at
 *   0x0a1b57ea… (selectors 0xf93d4f8a / 0x2e17de78 / 0x012ce501). See
 *   SUPERNOVA_ABI_FACTS.md. Nothing here is inferred from a name.
 *
 * What is a labelled STUB (and why):
 *   acquire           — purchase seam, behind SUPERNOVA_BUY_ENABLED (OFF).
 *
 * TWO INVARIANTS THIS MODULE EXISTS TO ENFORCE:
 *
 *  1. NON-CUSTODIAL. Nothing here signs anything. Every state-changing action
 *     is a transaction the member signs in their own wallet: we build calldata
 *     and hand it to THEIR wallet client over window.ethereum. This app never
 *     holds a key, never holds an NFT, and cannot move or return one — and
 *     there is no code path that could, because no private key ever exists in
 *     this process.
 *
 *  2. A FAILED READ IS NEVER A ZERO. Every failure path throws a typed
 *     SupernovaChainError. A member with a broken RPC connection must see "we
 *     couldn't read your balances", never "you own 0" — the second is a lie
 *     that looks like data. A genuine on-chain balance of 0 is, of course,
 *     fine: it came back from the chain.
 */

/* BigInt is an ES2020 built-in and every browser in our browserslist has it,
   but the CRA eslint env doesn't declare it — without this, `no-undef` fails
   the build on the token ids passed to balanceOfBatch. */
/* global BigInt */

/* ── Errors ─────────────────────────────────────────────────────────────────
   Defined in supernovaChainError.js and re-exported here, so the modules that
   only describe a refusal don't have to import viem to throw. Every existing
   `import { SUPERNOVA_ERROR, SupernovaChainError } from "./supernovaChain"`
   keeps working, against the same class object.                              */

export { SUPERNOVA_ERROR, SupernovaChainError };

/* ── Rejection detection ────────────────────────────────────────────────────
   Wallets disagree on how they say "the user pressed Cancel". EIP-1193 spec's
   4001 is the standard; ethers-style providers send ACTION_REJECTED; viem wraps
   it as UserRejectedRequestError. We check all of them and fall back to the
   message, because misreading a cancel as a failure is the difference between
   "try again when you're ready" and a red error a member didn't cause.        */

const REJECTION_NAMES = ["UserRejectedRequestError", "UserRejectedRequest"];

/** @param {unknown} error @returns {boolean} */
export function isUserRejection(error) {
  if (!error || typeof error !== "object") return false;
  const e = /** @type {any} */ (error);
  if (e.code === 4001 || e.code === "ACTION_REJECTED") return true;
  if (REJECTION_NAMES.includes(e.name)) return true;
  if (e.cause && e.cause !== e && isUserRejection(e.cause)) return true;
  const text = typeof e.message === "string" ? e.message : "";
  return /user (rejected|denied)|rejected the request|request rejected/i.test(text);
}

export const SIGNATURE_REJECTED_MESSAGE =
  "You declined the signature in your wallet. Nothing was submitted and " +
  "nothing changed on-chain — you can start again whenever you're ready.";

/**
 * Normalize anything thrown by a write path into a SupernovaChainError, so the
 * UI has exactly one error shape to render and a member-safe `message` on every
 * branch. Errors we already typed pass straight through.
 *
 * @param {unknown} error
 * @param {string} fallbackMessage member-safe copy for an unrecognized failure
 * @returns {SupernovaChainError}
 */
export function toSupernovaWriteError(error, fallbackMessage) {
  if (error instanceof SupernovaChainError) return error;
  if (isUserRejection(error)) {
    return new SupernovaChainError(
      SUPERNOVA_ERROR.USER_REJECTED,
      SIGNATURE_REJECTED_MESSAGE,
      error
    );
  }
  return new SupernovaChainError(SUPERNOVA_ERROR.WRITE_FAILED, fallbackMessage, error);
}

/* ── Client ─────────────────────────────────────────────────────────────── */

let cachedClient = null;

/**
 * A read-only Arbitrum client. Mirrors useArbitrumWallet's approach: prefer the
 * member's injected provider, fall back to viem's public transport. Reads only
 * — this client is never used to send a transaction.
 */
export function createSupernovaPublicClient() {
  if (cachedClient) return cachedClient;
  const injected = typeof window !== "undefined" ? window.ethereum : null;
  try {
    cachedClient = createPublicClient({
      chain: arbitrum,
      transport: injected ? custom(injected) : http(),
    });
  } catch (e) {
    try {
      cachedClient = createPublicClient({ chain: arbitrum, transport: http() });
    } catch (fallbackError) {
      throw new SupernovaChainError(
        SUPERNOVA_ERROR.NO_CLIENT,
        "Couldn't reach Arbitrum. Check your connection and try again.",
        fallbackError
      );
    }
  }
  return cachedClient;
}

/** Test seam — drop the memoized client. */
export function resetSupernovaClient() {
  cachedClient = null;
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function requireWallet(wallet) {
  try {
    return getAddress(wallet);
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.INVALID_WALLET,
      "Connect a wallet to see your Supernova collection.",
      e
    );
  }
}

/**
 * Coerce one balanceOfBatch entry to a count. Anything we can't read as a
 * whole non-negative number is malformed — we throw instead of defaulting,
 * because the default would be a zero that reads as fact.
 */
function toCount(value) {
  if (typeof value === "bigint") {
    if (value < 0 || value > Number.MAX_SAFE_INTEGER) {
      throw new SupernovaChainError(
        SUPERNOVA_ERROR.CHAIN_READ_MALFORMED,
        "Your Supernova balances came back in an unexpected format."
      );
    }
    return Number(value);
  }
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) {
    return Number(value);
  }
  throw new SupernovaChainError(
    SUPERNOVA_ERROR.CHAIN_READ_MALFORMED,
    "Your Supernova balances came back in an unexpected format."
  );
}

/* ── getOwned — REAL chain read ─────────────────────────────────────────── */

/**
 * @typedef {Object} OwnedToken
 * @property {number} id     ERC-1155 token id (7–10)
 * @property {string} name   collection name for that id
 * @property {number} owned  balance held by the wallet
 *
 * @typedef {Object} OwnedResult
 * @property {string} wallet        checksummed address that was read
 * @property {number} chainId
 * @property {string} contract      collection address
 * @property {OwnedToken[]} tokens
 * @property {number} totalOwned
 * @property {number} asOfMs
 */

/**
 * Read the wallet's balance of every Supernova token id in ONE call
 * (balanceOfBatch — four ids, one round trip).
 *
 * @param {string} wallet
 * @param {{client?: object}} [options] `client` is a test/injection seam
 * @returns {Promise<OwnedResult>}
 * @throws {SupernovaChainError} on a bad address, a failed read, or a
 *         malformed response — NEVER a zeroed result.
 */
export async function getOwned(wallet, options = {}) {
  const address = requireWallet(wallet);
  const client = options.client || createSupernovaPublicClient();

  let balances;
  try {
    balances = await client.readContract({
      address: getAddress(SUPERNOVA_COLLECTION_ADDRESS),
      abi: parseAbi(ERC1155_BALANCE_ABI),
      functionName: "balanceOfBatch",
      args: [
        SUPERNOVA_TOKEN_IDS.map(() => address),
        SUPERNOVA_TOKEN_IDS.map((id) => BigInt(id)),
      ],
    });
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_FAILED,
      "Couldn't read your Supernova collection from Arbitrum. Try again.",
      e
    );
  }

  if (!Array.isArray(balances) || balances.length !== SUPERNOVA_TOKENS.length) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_MALFORMED,
      "Your Supernova balances came back in an unexpected format."
    );
  }

  const tokens = SUPERNOVA_TOKENS.map((token, i) => ({
    id: token.id,
    name: token.name,
    owned: toCount(balances[i]),
  }));

  return {
    wallet: address,
    chainId: SUPERNOVA_CHAIN_ID,
    contract: getAddress(SUPERNOVA_COLLECTION_ADDRESS),
    tokens,
    totalOwned: tokens.reduce((sum, t) => sum + t.owned, 0),
    asOfMs: Date.now(),
  };
}

/* ── getApprovalStatus — REAL chain read ────────────────────────────────── */

/**
 * @typedef {Object} ApprovalStatus
 * @property {string} wallet     checksummed address that was read
 * @property {string} collection ERC-1155 collection
 * @property {string} operator   staking contract, the operator being approved
 * @property {boolean} approved  true ⇒ no approval transaction is needed
 * @property {number} asOfMs
 */

/**
 * Has this wallet already approved the staking contract as an operator on the
 * Supernova collection?
 *
 * This is the gate on the FIRST stake and only the first: ERC-1155 approval is
 * per operator, not per token, so a member who staked through the old dApp is
 * already approved and never sees the step. `isApprovedForAll` is a view — this
 * function reads, it never approves.
 *
 * Same invariant as every other read here: a failure throws. "We couldn't check
 * your approval" must not render as "you haven't approved", which would send a
 * member to sign a transaction they don't need.
 *
 * @param {string} wallet
 * @param {{client?: object}} [options] `client` is a test/injection seam
 * @returns {Promise<ApprovalStatus>}
 * @throws {SupernovaChainError}
 */
export async function getApprovalStatus(wallet, options = {}) {
  const address = requireWallet(wallet);
  const client = options.client || createSupernovaPublicClient();
  const collection = getAddress(SUPERNOVA_COLLECTION_ADDRESS);
  const operator = getAddress(SUPERNOVA_STAKING_ADDRESS);

  let approved;
  try {
    approved = await client.readContract({
      address: collection,
      abi: parseAbi(ERC1155_APPROVAL_ABI),
      functionName: "isApprovedForAll",
      args: [address, operator],
    });
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_FAILED,
      "Couldn't check whether you've approved the staking contract. Try again.",
      e
    );
  }

  if (typeof approved !== "boolean") {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_MALFORMED,
      "The approval check came back in an unexpected format."
    );
  }

  return { wallet: address, collection, operator, approved, asOfMs: Date.now() };
}

/* ── getStaked — wired, but unavailable until the read ABI is verified ──── */

/**
 * @typedef {Object} StakeLotRecord
 * @property {string} lotId
 * @property {number} tokenId
 * @property {number} qty
 * @property {number} lockDays
 * @property {number} stakedAtMs
 * @property {number} unlockAtMs
 * @property {"ACTIVE"|"MATURED_OPEN"} status
 */

/**
 * Turn one decoded lot into the shape the XP model consumes. Every field is
 * required: a lot missing its lock duration (V-2) cannot be rated, and
 * defaulting it would silently invent a multiplier.
 *
 * @returns {StakeLotRecord}
 */
export function normalizeStakeLot(raw, nowMs = Date.now()) {
  if (!raw || typeof raw !== "object") {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
      "We couldn't read your stakes from the contract."
    );
  }
  let lot;
  try {
    // normalizeLot enforces qty / lockDays / stakedAtMs and derives unlockAtMs.
    lot = normalizeLot({
      qty: typeof raw.qty === "bigint" ? Number(raw.qty) : raw.qty,
      lockDays: typeof raw.lockDays === "bigint" ? Number(raw.lockDays) : raw.lockDays,
      stakedAtMs:
        typeof raw.stakedAtMs === "bigint" ? Number(raw.stakedAtMs) : raw.stakedAtMs,
      unlockAtMs:
        typeof raw.unlockAtMs === "bigint" ? Number(raw.unlockAtMs) : raw.unlockAtMs,
    });
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
      "We couldn't read your stakes from the contract.",
      e
    );
  }
  const tokenId = typeof raw.tokenId === "bigint" ? Number(raw.tokenId) : raw.tokenId;
  if (!SUPERNOVA_TOKEN_IDS.includes(tokenId)) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
      "We couldn't read your stakes from the contract.",
      new Error(`unexpected tokenId ${String(tokenId)}`)
    );
  }
  if (!raw.lotId) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
      "We couldn't read your stakes from the contract.",
      new Error("lot is missing an id")
    );
  }
  return {
    lotId: String(raw.lotId),
    tokenId,
    qty: lot.qty,
    lockDays: lot.lockDays,
    stakedAtMs: lot.stakedAtMs,
    unlockAtMs: lot.unlockAtMs,
    status: nowMs >= lot.unlockAtMs ? "MATURED_OPEN" : "ACTIVE",
  };
}

/**
 * @typedef {Object} StakedResult
 * @property {string} wallet
 * @property {number} chainId
 * @property {string} contract
 * @property {StakeLotRecord[]} lots
 * @property {number} stakedCount  total NFTs staked across all open lots
 * @property {number} asOfMs
 */

/**
 * How many `stakes(id)` reads are in flight at once. The contract has no batch
 * accessor, so a member who has ever opened N stakes costs N calls — kept in
 * small waves rather than one burst so a long history doesn't hammer the RPC.
 */
export const STAKE_LOT_READ_BATCH = 8;

/**
 * Hop 2 of the stake read: one `stakes(id)` per id, in waves.
 *
 * Returns the raw responses paired with the id that produced them, because the
 * id is the only place the contract's stakeId survives — and the exit writes are
 * signed against it.
 */
async function readStakeRecords(client, lotRead, stakeIds) {
  const entries = [];
  for (let i = 0; i < stakeIds.length; i += STAKE_LOT_READ_BATCH) {
    const wave = stakeIds.slice(i, i + STAKE_LOT_READ_BATCH);
    /* eslint-disable no-await-in-loop -- the waves are sequential on purpose */
    const records = await Promise.all(
      wave.map((stakeId) =>
        client.readContract({
          address: getAddress(lotRead.address || SUPERNOVA_STAKING_ADDRESS),
          abi: parseAbi(lotRead.abi),
          functionName: lotRead.functionName,
          args: lotRead.buildArgs ? lotRead.buildArgs(stakeId) : [stakeId],
        })
      )
    );
    /* eslint-enable no-await-in-loop */
    wave.forEach((stakeId, j) => entries.push({ stakeId, record: records[j] }));
  }
  return entries;
}

/**
 * Read the wallet's open stake lots from the staking contract.
 *
 * TWO HOPS, because the contract offers no single accessor for them:
 * `getUserStakes(address)` returns every stake id the wallet has EVER opened,
 * then `stakes(id)` returns the lot behind one id. supernovaStakeRead owns both
 * fragments and all of the decoding; this function owns the calls and the
 * failure modes.
 *
 * It does NOT return an empty list when it can't see — "no stakes" and "we can't
 * see your stakes" are different facts and the UI must be able to tell them
 * apart. With no read descriptor at all it throws STAKE_READ_UNAVAILABLE.
 *
 * @param {string} wallet
 * @param {{client?: object, read?: object, nowMs?: number}} [options]
 * @returns {Promise<StakedResult>}
 * @throws {SupernovaChainError}
 */
export async function getStaked(wallet, options = {}) {
  const address = requireWallet(wallet);
  const read = options.read === undefined ? SUPERNOVA_STAKING_READ : options.read;

  if (!read) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_READ_UNAVAILABLE,
      "Stake data isn't connected yet, so we can't show your stakes. " +
        "Nothing about your on-chain stakes has changed."
    );
  }

  const client = options.client || createSupernovaPublicClient();
  const nowMs = options.nowMs === undefined ? Date.now() : options.nowMs;

  let raw;
  try {
    raw = await client.readContract({
      address: getAddress(SUPERNOVA_STAKING_ADDRESS),
      abi: parseAbi(read.abi),
      functionName: read.functionName,
      args: read.buildArgs ? read.buildArgs(address) : [address],
    });
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_FAILED,
      "Couldn't read your stakes from Arbitrum. Try again.",
      e
    );
  }

  /* Hop 2, when the descriptor declares one. An id list we can't read is a
     failed read, not an empty one — hence the same typed errors as hop 1. */
  let entries = raw;
  if (read.lot) {
    let stakeIds;
    try {
      stakeIds = read.toStakeIds ? read.toStakeIds(raw) : raw;
    } catch (e) {
      throw new SupernovaChainError(
        SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
        "We couldn't read your stakes from the contract.",
        e
      );
    }
    try {
      entries = await readStakeRecords(client, read.lot, stakeIds);
    } catch (e) {
      throw new SupernovaChainError(
        SUPERNOVA_ERROR.CHAIN_READ_FAILED,
        "Couldn't read your stakes from Arbitrum. Try again.",
        e
      );
    }
  }

  let decoded;
  try {
    decoded = read.decodeLots(entries, address);
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
      "We couldn't read your stakes from the contract.",
      e
    );
  }
  if (!Array.isArray(decoded)) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_DECODE_FAILED,
      "We couldn't read your stakes from the contract."
    );
  }

  const lots = decoded.map((entry) => normalizeStakeLot(entry, nowMs));

  return {
    wallet: address,
    chainId: SUPERNOVA_CHAIN_ID,
    contract: getAddress(SUPERNOVA_STAKING_ADDRESS),
    lots,
    stakedCount: lots.reduce((sum, l) => sum + l.qty, 0),
    asOfMs: nowMs,
  };
}

/* ── getClosedStakes — the banked half, rebuilt from the event log ──────── */

/**
 * How many `getBlock` reads run at once, and the ceiling on how many exits we
 * will time. The waves mirror STAKE_LOT_READ_BATCH: one timestamp per exit
 * block, in small groups rather than one burst. The ceiling exists because the
 * busiest wallet in this contract's history closed 30-odd lots — a number that
 * needs no paging today, and a bound that keeps a future one from turning the
 * XP tile into a hundred round trips. Anything past it is EXCLUDED and counted,
 * never quietly dropped.
 */
export const EXIT_BLOCK_READ_BATCH = 8;
export const MAX_TIMED_EXITS = 120;

/**
 * @typedef {Object} ClosedStakesResult
 * @property {string} wallet
 * @property {number} chainId
 * @property {string} contract
 * @property {import("./supernovaClosedLots").ClosedLotRecord[]} lots
 * @property {number} excluded  closed lots we could see but could not rebuild
 * @property {number} asOfMs
 */

/**
 * Block timestamps for a set of exit blocks, in waves.
 *
 * ONE BAD BLOCK IS NOT A FAILED READ. A block that errors or answers without a
 * usable timestamp is simply left OUT of the map, which is what makes
 * buildClosedLots exclude and COUNT that lot rather than rate it — the member
 * still gets every other lot's XP plus a footnote naming what's missing. Losing
 * the whole banked half over one flaky block would be the worse answer.
 *
 * The caller decides what "all of them failed" means; this returns the count.
 */
async function readBlockTimes(client, blockKeys) {
  const times = {};
  let failed = 0;
  for (let i = 0; i < blockKeys.length; i += EXIT_BLOCK_READ_BATCH) {
    const wave = blockKeys.slice(i, i + EXIT_BLOCK_READ_BATCH);
    /* eslint-disable no-await-in-loop -- the waves are sequential on purpose */
    const settled = await Promise.allSettled(
      wave.map((key) =>
        client.getBlock({ blockNumber: BigInt(key), includeTransactions: false })
      )
    );
    /* eslint-enable no-await-in-loop */
    for (let j = 0; j < wave.length; j += 1) {
      const block = settled[j].status === "fulfilled" ? settled[j].value : null;
      const seconds = block ? Number(block.timestamp) : NaN;
      if (Number.isFinite(seconds) && seconds > 0) times[wave[j]] = seconds * 1000;
      else failed += 1;
    }
  }
  return { times, failed };
}

/**
 * Read the wallet's CLOSED stake lots — the half `getStaked` cannot see.
 *
 * `stakes()` keeps a closed lot's fields but not the moment it closed, and
 * without that there is no served duration and so no XP. The contract's event
 * log has both halves, so this is three `eth_getLogs` (open, normal exit,
 * emergency exit — the two exits are structurally identical and separable only
 * by topic0) plus one block read per exit block for the timestamp.
 *
 * IT NEVER RETURNS AN EMPTY LIST FOR A FAILED READ, for the same reason
 * getStaked doesn't: "you have closed no stakes" and "we couldn't read your
 * closed stakes" are different facts, and only the first one is data. A lot we
 * can see but cannot rebuild is counted in `excluded` and the tile footnotes it.
 *
 * @param {string} wallet
 * @param {{client?: object, nowMs?: number, fromBlock?: number|bigint}} [options]
 * @returns {Promise<ClosedStakesResult>}
 * @throws {SupernovaChainError}
 */
export async function getClosedStakes(wallet, options = {}) {
  const address = requireWallet(wallet);
  const client = options.client || createSupernovaPublicClient();
  const nowMs = options.nowMs === undefined ? Date.now() : options.nowMs;

  if (!client || typeof client.getLogs !== "function") {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.STAKE_READ_UNAVAILABLE,
      "Your closed stakes aren't connected yet, so we can't total the XP you " +
        "banked from them. Nothing about your on-chain stakes has changed."
    );
  }

  const fromBlock =
    options.fromBlock === undefined
      ? BigInt(SUPERNOVA_STAKING_DEPLOY_BLOCK)
      : BigInt(options.fromBlock);
  const logQuery = {
    address: getAddress(SUPERNOVA_STAKING_ADDRESS),
    args: { user: address },
    fromBlock,
    toBlock: "latest",
  };

  let openedLogs;
  let unstakedLogs;
  let emergencyLogs;
  try {
    [openedLogs, unstakedLogs, emergencyLogs] = await Promise.all([
      client.getLogs({ ...logQuery, event: parseAbiItem(TOKEN_STAKED_EVENT) }),
      client.getLogs({ ...logQuery, event: parseAbiItem(TOKEN_UNSTAKED_EVENT) }),
      client.getLogs({
        ...logQuery,
        event: parseAbiItem(TOKEN_EMERGENCY_UNSTAKED_EVENT),
      }),
    ]);
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_FAILED,
      "Couldn't read your closed stakes from Arbitrum. Try again.",
      e
    );
  }

  if (!Array.isArray(openedLogs) || !Array.isArray(unstakedLogs) || !Array.isArray(emergencyLogs)) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_MALFORMED,
      "Your closed stakes came back in an unexpected format."
    );
  }

  const exits = [
    ...unstakedLogs.map((log) => decodeExitLog(log, { emergency: false })),
    ...emergencyLogs.map((log) => decodeExitLog(log, { emergency: true })),
  ].filter(Boolean);

  /* Over the ceiling, the newest exits are the ones we time — and the rest are
     counted as excluded below rather than silently missing. */
  const ordered = [...exits].sort((a, b) => Number(b.blockKey) - Number(a.blockKey));
  const timed = ordered.slice(0, MAX_TIMED_EXITS);
  const untimed = ordered.length - timed.length;

  const blockKeys = exitBlockKeys(timed);
  const { times: blockTimesMs, failed } = await readBlockTimes(client, blockKeys);
  /* Every single block read failing is not "a few lots we couldn't rebuild" —
     it is a failed read, and saying "you banked nothing" would be a lie. */
  if (blockKeys.length > 0 && failed === blockKeys.length) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.CHAIN_READ_FAILED,
      "Couldn't read your closed stakes from Arbitrum. Try again."
    );
  }

  const { lots, excluded } = buildClosedLots({
    opened: openedLogs,
    exits: timed,
    blockTimesMs,
    wallet: address,
  });

  return {
    wallet: address,
    chainId: SUPERNOVA_CHAIN_ID,
    contract: getAddress(SUPERNOVA_STAKING_ADDRESS),
    lots,
    excluded: excluded + untimed,
    asOfMs: nowMs,
  };
}

/* ── Writes — member-signed, with an honest fallback ────────────────────── */

/**
 * @typedef {Object} StakeRequest
 * @property {string} wallet   member's address (they sign; we never do)
 * @property {number} tokenId  7–10
 * @property {number} qty      NFTs to lock
 * @property {number} lockDays one of LOCK_PRESETS
 *
 * @typedef {Object} UnstakeRequest
 * @property {string} wallet
 * @property {string} lotId    carries the contract's stakeId
 * @property {string|number} [stakeId] the stake id outright, if the caller has it
 * @property {boolean} [emergency] true ⇒ emergencyUnstake, the pre-unlock exit
 * @property {number} [qty]    omit for the whole lot. THE CONTRACT HAS NO
 *        PARTIAL EXIT: unstake(stakeId) closes the whole lot, so a qty here is
 *        accepted only to be ignored, never quietly rounded into something else
 *
 * @typedef {Object} AcquireRequest
 * @property {string} wallet
 * @property {number} tokenId
 * @property {number} qty
 *
 * THE WRITE CONTRACT every function below resolves to when it is implemented.
 * The UI's four transaction states map onto it directly, so the shape is fixed
 * here even though nothing produces one yet:
 *
 *   PENDING    the promise is in flight — the member is looking at their
 *              wallet's signature prompt. Nothing has been broadcast.
 *   REJECTED   the promise rejects with code USER_REJECTED (isUserRejection).
 *   SIGNED     the promise RESOLVES: they signed, and `hash` is on the wire.
 *   CONFIRMED  `confirmed` resolves — the transaction is mined.
 *
 * `confirmed` is REQUIRED. Without it a caller can only ever say "submitted",
 * because claiming a stake is confirmed before it is mined would be a fake
 * success — the one thing this flow must never show.
 *
 * @typedef {Object} TxResult
 * @property {`0x${string}`} hash    the member-signed transaction hash
 * @property {number} chainId
 * @property {Promise<{status: string}>} confirmed resolves once mined
 */

const notImplemented = (what) =>
  new SupernovaChainError(
    SUPERNOVA_ERROR.NOT_IMPLEMENTED,
    `${what} isn't available in the app yet.`
  );

/**
 * THE FALLBACK. Every write below can end here, and it is the ONLY thing this
 * module does when it cannot be certain the transaction it would put in front of
 * a member is the right one. It carries NOT_IMPLEMENTED, which the stake and
 * unstake modals already render as a plainly-labelled "signing is unavailable in
 * this build" dead end — never a spinner, never a fake confirmation.
 *
 * `reason` is member-safe copy that reads as the back half of a sentence,
 * because that is exactly where the modals put it.
 */
const signingUnavailable = (reason, cause) =>
  new SupernovaChainError(SUPERNOVA_ERROR.NOT_IMPLEMENTED, reason, cause);

const NO_ABI_REASON =
  "the verified staking-contract interface isn't part of this build, so there " +
  "is nothing safe to sign against.";
const NO_WALLET_REASON =
  "no browser wallet is connected here, so there is nothing to sign with.";
/* A preflight that doesn't come back clean means the transaction we were about
   to offer would revert. We do NOT hand it to the member anyway and let their
   wallet eat the gas: we stop, and say we stopped.
   This is a real FAILURE, not an "unavailable build" — the signing path exists,
   this particular transaction just wouldn't land. Saying "signing is
   unavailable" here would be a lie in the member's favour and would send them
   away from a problem they can often fix (wrong account, already-closed lot,
   not enough NFTs). */
const PREFLIGHT_FAILED_MESSAGE =
  "We ran this against the staking contract first and it came back as one that " +
  "would fail, so we did NOT ask you to sign it. Nothing was submitted, nothing " +
  "changed on-chain, and you were not charged any gas. Reload the tab to " +
  "refresh your balances and stakes, then try again.";

const WRONG_CHAIN_MESSAGE =
  "Your wallet is on another network. Switch it to Arbitrum One and try again " +
  "— nothing was submitted.";

/** The provider the MEMBER's wallet exposes. Injectable for tests. */
const walletProvider = (options) => {
  if (options && options.provider) return options.provider;
  return typeof window !== "undefined" && window.ethereum ? window.ethereum : null;
};

const sameAddress = (a, b) =>
  typeof a === "string" && typeof b === "string" && a.toLowerCase() === b.toLowerCase();

/**
 * Everything a member-signed write needs, or a typed refusal.
 *
 * The order of the checks is the point: the two that can end in the fallback
 * (no interface, no wallet) come first and cost nothing, so a build that cannot
 * sign says so without touching the member's wallet at all. `eth_chainId` and
 * `eth_accounts` never prompt.
 *
 * @param {string} wallet
 * @param {Object} options provider / client / walletClient / stakingAbi seams
 * @returns {Promise<{account: string, abi: Array, publicClient: Object,
 *                    walletClient: Object}>}
 */
async function memberWriteContext(wallet, options = {}) {
  const account = requireWallet(wallet);

  const abi =
    options.stakingAbi === undefined ? getStakingWriteAbi() : options.stakingAbi;
  if (!abi) throw signingUnavailable(NO_ABI_REASON);

  const provider = walletProvider(options);
  if (!provider) throw signingUnavailable(NO_WALLET_REASON);

  /* Reads, not prompts. A provider that won't answer either of these tells us
     nothing, so neither is treated as a failure — viem's own chain check on the
     wallet client is the backstop. */
  let hexChainId = null;
  try {
    hexChainId = await provider.request({ method: "eth_chainId" });
  } catch (e) {
    hexChainId = null;
  }
  if (hexChainId && parseInt(hexChainId, 16) !== SUPERNOVA_CHAIN_ID) {
    throw new SupernovaChainError(SUPERNOVA_ERROR.WRONG_CHAIN, WRONG_CHAIN_MESSAGE);
  }

  try {
    const accounts = await provider.request({ method: "eth_accounts" });
    if (Array.isArray(accounts) && accounts.length > 0) {
      /* The wallet moved to a different account since the tab read it. Signing
         from the wrong account would either revert or, worse, work — on
         somebody else's position. */
      if (!accounts.some((a) => sameAddress(a, account))) {
        throw new SupernovaChainError(
          SUPERNOVA_ERROR.WRITE_FAILED,
          "Your wallet is on a different account than the one this tab is " +
            "showing. Switch back to it (or reload the tab) and try again — " +
            "nothing was submitted."
        );
      }
    }
  } catch (e) {
    if (e instanceof SupernovaChainError) throw e;
    /* eth_accounts is a courtesy check; a provider that won't answer it doesn't
       stop the member from signing in their own wallet. */
  }

  const publicClient = options.client || createSupernovaPublicClient();

  let walletClient = options.walletClient;
  if (!walletClient) {
    try {
      walletClient = createWalletClient({
        account,
        chain: arbitrum,
        transport: custom(provider),
      });
    } catch (e) {
      throw signingUnavailable(NO_WALLET_REASON, e);
    }
  }

  return { account, abi, publicClient, walletClient };
}

/**
 * Guard, dry-run, then let the MEMBER sign. This is the only function in the app
 * that results in a transaction, and it still doesn't sign one: `writeContract`
 * on a wallet client is a request to the member's wallet, which is where the key
 * is. ARC Educational holds no key and cannot sign for anyone.
 *
 * THE ORDER IS THE SAFETY PROPERTY, and it is strict:
 *
 *   1. TARGET GUARD. `to` must be the compiled-in constant for this call —
 *      supernovaWriteGuards.assertWriteTarget. Cheap, local, and it runs before
 *      anything leaves the browser, so a transaction aimed at the wrong contract
 *      never even gets simulated, let alone signed.
 *   2. DRY RUN. `simulateContract` is an eth_call against the live contract from
 *      the member's own address, and `estimateContractGas` is the second opinion
 *      on the same question. Both cost nothing and change nothing. A transaction
 *      that would revert stops here.
 *   3. THE MEMBER'S WALLET. Only now, and only once.
 *
 * NOTHING RETRIES. If any step fails, the promise rejects and the flow stops.
 * The member re-runs it themselves or they don't.
 *
 * @param {"staking"|"collection"} expectTarget which constant `address` must be
 * @returns {Promise<TxResult>}
 */
async function sendMemberSignedWrite({
  context,
  address,
  abi,
  functionName,
  args,
  failedMessage,
  expectTarget,
}) {
  const { account, publicClient, walletClient } = context;

  /* 1. Hardcoded target assertion — throws GUARD_FAILED before any network I/O. */
  assertWriteTarget(address, expectTarget, functionName);

  /* 2. Dry run. Two independent reads of "would this revert?", so a provider
        that answers one and not the other still can't wave a bad tx through. */
  let request = null;
  try {
    const simulated = await publicClient.simulateContract({
      address,
      abi,
      functionName,
      args,
      account,
    });
    request = simulated && simulated.request ? simulated.request : null;
  } catch (e) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.WRITE_FAILED,
      PREFLIGHT_FAILED_MESSAGE,
      e
    );
  }

  if (typeof publicClient.estimateContractGas === "function") {
    try {
      await publicClient.estimateContractGas({ address, abi, functionName, args, account });
    } catch (e) {
      throw new SupernovaChainError(
        SUPERNOVA_ERROR.WRITE_FAILED,
        PREFLIGHT_FAILED_MESSAGE,
        e
      );
    }
  }

  /* The simulated request is what actually gets signed, so it is guarded too —
     the address we checked in step 1 and the address on the wire are only the
     same thing if we check that they are. */
  if (request && request.address) {
    assertWriteTarget(request.address, expectTarget, `${functionName} (simulated)`);
  }

  /* 3. The member signs, in their own wallet. */
  let hash;
  try {
    hash = await walletClient.writeContract(
      request || { address, abi, functionName, args, account, chain: arbitrum }
    );
  } catch (e) {
    /* A decline lands here and comes back as USER_REJECTED — a decision, not a
       failure, and fully recoverable. */
    throw toSupernovaWriteError(e, failedMessage);
  }

  if (!hash) {
    throw new SupernovaChainError(SUPERNOVA_ERROR.WRITE_FAILED, failedMessage);
  }

  return {
    hash,
    chainId: SUPERNOVA_CHAIN_ID,
    confirmed: watchConfirmation(publicClient, hash, failedMessage),
  };
}

/**
 * The `confirmed` half of the write contract: resolves when the transaction is
 * MINED and succeeded, rejects if it reverted.
 *
 * Returns undefined when the client can't watch — the callers then stop at
 * "submitted" rather than promoting a hash to a confirmation, which is the one
 * thing this flow must never do.
 */
function watchConfirmation(publicClient, hash, failedMessage) {
  if (!publicClient || typeof publicClient.waitForTransactionReceipt !== "function") {
    return undefined;
  }
  return publicClient
    .waitForTransactionReceipt({ hash })
    .catch((e) => {
      throw new SupernovaChainError(
        SUPERNOVA_ERROR.WRITE_FAILED,
        "We lost track of your transaction before it was confirmed. Check your " +
          "wallet or a block explorer for the final result.",
        e
      );
    })
    .then((receipt) => {
      if (!receipt || receipt.status !== "success") {
        throw new SupernovaChainError(SUPERNOVA_ERROR.WRITE_FAILED, failedMessage, receipt);
      }
      return { status: "success", receipt };
    });
}

/**
 * Approve the staking contract as an operator on the Supernova collection —
 * the one-time step before a member's first stake, signed by the member.
 *
 * WHAT THE MEMBER IS ACTUALLY GRANTING: permission for one contract (the
 * staking contract) to move Supernovas out of their wallet. Not a transfer, not
 * a spend, and nothing to ARC Educational — we cannot sign for anyone. The stake
 * modal explains exactly this before the button is ever offered, and it is
 * revocable from their wallet at any time.
 *
 * Deliberately gated on the staking write ABI as well as its own: an approval
 * granted for a stake path this build can't complete is pure downside for the
 * member, so if we can't stake, we don't ask for permission either.
 *
 * @param {{wallet: string}} request
 * @param {Object} [options] provider / client / walletClient / stakingAbi seams
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError}
 */
export async function setApprovalForAll(request, options = {}) {
  const context = await memberWriteContext(request && request.wallet, options);

  const operator = getAddress(SUPERNOVA_STAKING_ADDRESS);
  const args = [operator, true];

  /* The payload is what the flow described (and logged); the guard proves the
     calldata below is that same transaction and not a lookalike. */
  if (options.payload) {
    assertApprovalPayloadMatches(options.payload, request || {}, {
      operator,
      approved: true,
    });
  }

  return sendMemberSignedWrite({
    context,
    address: getAddress(SUPERNOVA_COLLECTION_ADDRESS),
    expectTarget: "collection",
    abi: parseAbi(ERC1155_APPROVAL_ABI),
    functionName: "setApprovalForAll",
    args,
    failedMessage:
      "Couldn't send the approval. Nothing changed and nothing was approved — " +
      "try again.",
  });
}

/**
 * Lock NFTs into the staking contract:
 * `stake(address token, uint256 tokenId, uint256 amount, uint256 duration)`.
 *
 * THE UNITS ARE NOT THE FORM'S UNITS. The member picks a quantity and a number
 * of days; the contract takes a raw ERC-1155 amount and a duration in SECONDS.
 * supernovaWriteArgs does that mapping and refuses anything it can't map — a
 * coerced argument here would be a signature over a stake nobody asked for.
 *
 * @param {StakeRequest} request
 * @param {Object} [options] provider / client / walletClient / stakingAbi seams
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError}
 */
export async function stake(request, options = {}) {
  const { wallet, tokenId, qty, lockDays } = request || {};
  const context = await memberWriteContext(wallet, options);

  const tokenIdArg = stakeTokenIdArg(tokenId);
  const amountArg = stakeAmountArg(qty);
  const durationArg = stakeDurationSeconds(lockDays);
  if (tokenIdArg === null || amountArg === null || durationArg === null) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.WRITE_FAILED,
      "We couldn't turn those choices into a transaction the staking contract " +
        "accepts, so nothing was submitted. Check the type, the quantity and " +
        "the lock, and try again."
    );
  }

  /* The collection address is the contract's own first argument — it stakes any
     whitelisted ERC-1155, and the Supernova collection is one of four. Getting
     this wrong would stake against a collection the member never picked, so it
     is a constant here and an assertion in the guard. */
  const token = getAddress(SUPERNOVA_COLLECTION_ADDRESS);

  /* Every argument re-derived above is compared against the payload the flow
     showed and logged. One digit of drift and nobody is asked to sign. */
  if (options.payload) {
    assertStakePayloadMatches(options.payload, request || {}, {
      token,
      tokenId: tokenIdArg,
      amount: amountArg,
      duration: durationArg,
    });
  }

  return sendMemberSignedWrite({
    context,
    address: getAddress(SUPERNOVA_STAKING_ADDRESS),
    expectTarget: "staking",
    abi: context.abi,
    args: [token, tokenIdArg, amountArg, durationArg],
    functionName: "stake",
    failedMessage:
      "Couldn't submit your stake. Nothing was locked and your NFTs are still " +
      "in your wallet — try again.",
  });
}

/**
 * Close a lot: `unstake(uint256 stakeId)`, or `emergencyUnstake(uint256)` when
 * the caller asks for the pre-unlock exit.
 *
 * WHY THE CALLER DECIDES. The two functions are indistinguishable after the
 * fact — the contract's own status enum reads 1 for both, and only the event
 * topic tells them apart (SUPERNOVA_ABI_FACTS.md, open item 1). So the flow that
 * showed the member what they were giving up is the flow that says which exit
 * they chose; this layer does not infer it.
 *
 * NO PENALTY LIVES HERE. This contract has no penalty parameter, no penalty
 * accessor and no penalty field on the emergency event. The early-exit figures
 * the modal shows are the XP MODEL's, in-app points only — what the contract
 * releases is the contract's business.
 *
 * @param {UnstakeRequest} request
 * @param {Object} [options] provider / client / walletClient / stakingAbi seams
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError}
 */
export async function unstake(request, options = {}) {
  if (request && request.emergency) return emergencyUnstake(request, options);
  return closeLot(request, options, {
    functionName: "unstake",
    failedMessage:
      "Couldn't submit your unstake. Nothing was released and your stake is " +
      "untouched — try again.",
  });
}

/**
 * The pre-unlock exit: `emergencyUnstake(uint256 stakeId)`. 187 of the 297
 * stakes this contract has ever held left this way, so it is a normal path —
 * but it is the one that ends a lock early, and the modal has already shown the
 * member what that costs them in XP before anything reaches this function.
 *
 * @param {UnstakeRequest} request
 * @param {Object} [options]
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError}
 */
export async function emergencyUnstake(request, options = {}) {
  return closeLot(request, options, {
    functionName: "emergencyUnstake",
    failedMessage:
      "Couldn't submit your early unstake. Nothing was released and your stake " +
      "is untouched — try again.",
  });
}

/** Shared body of the two exits — they differ only by which function is called. */
async function closeLot(request, options, { functionName, failedMessage }) {
  const { wallet, lotId, stakeId } = request || {};
  const context = await memberWriteContext(wallet, options);

  const stakeIdArgument = stakeIdArg(stakeId === undefined ? lotId : stakeId);
  if (stakeIdArgument === null) {
    /* We don't know which position we'd be closing, and every stake id on this
       contract belongs to somebody. Refusing is the only safe answer. */
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.WRITE_FAILED,
      "We couldn't identify which stake to close, so nothing was submitted. " +
        "Reload the tab and try again."
    );
  }

  /* Which exit was chosen matters as much as which lot: the two functions take
     identical calldata, so only this check ties the signature back to the screen
     that explained what the member was giving up. */
  if (options.payload) {
    assertUnstakePayloadMatches(options.payload, request || {}, {
      functionName,
      stakeId: stakeIdArgument,
    });
  }

  return sendMemberSignedWrite({
    context,
    address: getAddress(SUPERNOVA_STAKING_ADDRESS),
    expectTarget: "staking",
    abi: context.abi,
    functionName,
    args: [stakeIdArgument],
    failedMessage,
  });
}

/**
 * Acquire seam — where a member would go to obtain a Supernova NFT.
 *
 * TODO(backend, supervised): NOT IMPLEMENTED and gated behind
 * SUPERNOVA_BUY_ENABLED (default OFF). No venue, no price feed, and no purchase
 * path is wired; when one is, it is an external, member-signed flow.
 *
 * @param {AcquireRequest} _request
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError} always
 */
export async function acquire(_request) {
  if (!isSupernovaBuyEnabled()) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.BUY_DISABLED,
      "Acquiring Supernova NFTs isn't available in the app."
    );
  }
  throw notImplemented("Acquiring Supernova NFTs");
}

/* ── The signing seam — what the UI actually calls ──────────────────────────
   These three are the ONLY entry points the modals and hooks use. Each one runs
   the same four steps in the same order, and the order is the safety property:

     1. DESCRIBE.  Build the payload from the member's choices, in the contract's
                   units. An unmappable choice dies here, before anything else.
     2. LOG.       Write that description to the console, so there is a record of
                   what was put in front of the member.
     3. GUARD.     Hand the payload to the write, which re-derives every argument
                   independently and refuses to proceed if the two disagree — or
                   if the target address isn't the compiled-in constant.
     4. SIGN.      The member's own wallet, once, after a dry run.

   Nothing here retries, batches, queues, or signs on anyone's behalf. One call,
   one prompt, one outcome.                                                    */

/**
 * Stake — the full path from the member's choices to their wallet.
 *
 * @param {StakeRequest} request the modal's state: wallet, tokenId, qty, lockDays
 * @param {Object} [options] provider / client / walletClient / stakingAbi seams
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError} GUARD_FAILED, WRITE_FAILED, USER_REJECTED, …
 */
export async function requestStakeSignature(request, options = {}) {
  const payload = logSignatureRequest(buildStakeSignaturePayload(request));
  return stake(request, { ...options, payload });
}

/**
 * Unstake, or the pre-unlock exit when `request.emergency` is set — decided by
 * the surface that told the member which exit they were taking, never inferred.
 *
 * @param {UnstakeRequest} request
 * @param {Object} [options]
 * @returns {Promise<TxResult>}
 * @throws {SupernovaChainError}
 */
export async function requestUnstakeSignature(request, options = {}) {
  const payload = logSignatureRequest(buildUnstakeSignaturePayload(request));
  return unstake(request, { ...options, payload });
}

/**
 * The one-time ERC-1155 operator approval — asked for ONLY when it isn't already
 * granted.
 *
 * `isApprovedForAll` is read first, every time, and a wallet that already
 * approved the staking contract is returned `{alreadyApproved: true}` without a
 * wallet prompt ever opening. 53 wallets have granted this approval already
 * (SUPERNOVA_ABI_FACTS.md); asking them again would be a real fee for a
 * transaction that does nothing.
 *
 * A FAILED CHECK IS NOT A "NO". If we can't read the approval we throw rather
 * than prompting: "we couldn't check" must not turn into a redundant transaction
 * the member pays for. The modal already offers them a Check again.
 *
 * @param {{wallet: string}} request
 * @param {Object} [options]
 * @returns {Promise<TxResult|{alreadyApproved: true, wallet: string}>}
 * @throws {SupernovaChainError}
 */
export async function requestApprovalSignature(request, options = {}) {
  const wallet = request && request.wallet;

  const status = await getApprovalStatus(wallet, options);
  if (status.approved === true) {
    return { alreadyApproved: true, wallet: status.wallet };
  }

  const payload = logSignatureRequest(buildApprovalSignaturePayload(request));
  return setApprovalForAll(request, { ...options, payload });
}

/**
 * The service surface the stake flow talks to. useSupernovaStake takes this
 * object as an injectable dependency, so the flow's state machine can be tested
 * against a fake without any of it reaching a chain.
 *
 * THE HOOKS CALL THE request* FUNCTIONS, NOT the raw writes below them. That is
 * not a formality: the request* wrappers are where the payload is built, logged
 * and handed to the guards. Calling `stake` or `setApprovalForAll` directly
 * skips the payload cross-check — the target-address guard still fires, but the
 * "does this calldata match what the member saw" one cannot, because there is
 * nothing to compare against. Keep the UI on the request* seam.
 */
const supernovaChain = {
  getOwned,
  getStaked,
  getClosedStakes,
  getApprovalStatus,
  requestApprovalSignature,
  requestStakeSignature,
  requestUnstakeSignature,
  setApprovalForAll,
  stake,
  unstake,
  emergencyUnstake,
  acquire,
};

export default supernovaChain;
